import React, { useEffect, useState } from 'react';
import { XIcon, CreditCardIcon, CheckCircleIcon, SparklesIcon, ShieldCheckIcon, LockIcon, PartyPopperIcon, RocketIcon, ZapIcon } from 'lucide-react';
import { CartItem, useBillingCart } from '../../context/BillingCartContext';
interface PaymentFlowModalProps {
  isOpen: boolean;
  onClose: () => void;
  items: CartItem[];
  total: number;
  onPaymentSuccess: () => void;
}
type PaymentStep = 'review' | 'processing' | 'success';
export function PaymentFlowModal({
  isOpen,
  onClose,
  items,
  total,
  onPaymentSuccess
}: PaymentFlowModalProps) {
  const [step, setStep] = useState<PaymentStep>('review');
  const [selectedMethod, setSelectedMethod] = useState('card');
  const {
    walletBalance,
    setWalletBalance
  } = useBillingCart();
  useEffect(() => {
    if (isOpen) {
      setStep('review');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  const handlePay = () => {
    setStep('processing');
    // Simulate payment processing
    setTimeout(() => {
      // Update wallet balance if wallet top-up was in cart
      const walletItem = items.find(item => item.type === 'wallet');
      if (walletItem) {
        setWalletBalance(walletBalance + walletItem.price);
      }
      setStep('success');
      // Auto close after success animation
      setTimeout(() => {
        onPaymentSuccess();
        onClose();
      }, 4000);
    }, 2000);
  };
  const getServicesList = () => {
    return items.map(item => {
      if (item.type === 'subscription') return `${item.name} Subscription`;
      if (item.type === 'wallet') return `₹${item.price.toLocaleString()} Media Budget`;
      return item.name;
    });
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm" data-id="element-201">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()} data-id="element-202">
        {/* Review Step */}
        {step === 'review' && <>
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex items-center justify-between" data-id="element-203">
              <h2 className="text-white font-bold text-lg" data-id="element-204">Complete Payment</h2>
              <button onClick={onClose} className="text-white/80 hover:text-white transition-colors" data-id="element-205">
                <XIcon size={20} data-id="element-206" />
              </button>
            </div>

            <div className="p-6" data-id="element-207">
              {/* Order Summary */}
              <div className="mb-6" data-id="element-208">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3" data-id="element-209">
                  Order Summary
                </h3>
                <div className="bg-gray-50 rounded-xl p-4 space-y-3" data-id="element-210">
                  {items.map(item => <div key={item.id} className="flex justify-between items-center" data-id="element-211">
                      <div data-id="element-212">
                        <p className="text-sm font-medium text-gray-900" data-id="element-213">
                          {item.name}
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-214">
                          {item.description}
                        </p>
                      </div>
                      <span className="text-sm font-semibold text-gray-900" data-id="element-215">
                        ₹{item.price.toLocaleString()}
                      </span>
                    </div>)}
                  <div className="border-t border-gray-200 pt-3 flex justify-between items-center" data-id="element-216">
                    <span className="font-semibold text-gray-900" data-id="element-217">Total</span>
                    <span className="text-xl font-bold text-gray-900" data-id="element-218">
                      ₹{total.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="mb-6" data-id="element-219">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3" data-id="element-220">
                  Payment Method
                </h3>
                <div className="space-y-2" data-id="element-221">
                  <label className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${selectedMethod === 'card' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-222">
                    <input type="radio" name="payment" value="card" checked={selectedMethod === 'card'} onChange={() => setSelectedMethod('card')} className="sr-only" data-id="element-223" />
                    <div className="w-10 h-6 bg-gradient-to-r from-blue-600 to-blue-400 rounded flex items-center justify-center mr-3" data-id="element-224">
                      <span className="text-white text-[8px] font-bold" data-id="element-225">
                        VISA
                      </span>
                    </div>
                    <div className="flex-1" data-id="element-226">
                      <p className="text-sm font-medium text-gray-900" data-id="element-227">
                        Visa ending in 4242
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-228">Expires 12/2027</p>
                    </div>
                    {selectedMethod === 'card' && <CheckCircleIcon className="text-blue-500" size={20} data-id="element-229" />}
                  </label>

                  <label className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${selectedMethod === 'upi' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-230">
                    <input type="radio" name="payment" value="upi" checked={selectedMethod === 'upi'} onChange={() => setSelectedMethod('upi')} className="sr-only" data-id="element-231" />
                    <div className="w-10 h-6 bg-gradient-to-r from-green-600 to-green-400 rounded flex items-center justify-center mr-3" data-id="element-232">
                      <span className="text-white text-[8px] font-bold" data-id="element-233">
                        UPI
                      </span>
                    </div>
                    <div className="flex-1" data-id="element-234">
                      <p className="text-sm font-medium text-gray-900" data-id="element-235">
                        9876543210@upi
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-236">Google Pay</p>
                    </div>
                    {selectedMethod === 'upi' && <CheckCircleIcon className="text-blue-500" size={20} data-id="element-237" />}
                  </label>
                </div>
              </div>

              {/* Security Badge */}
              <div className="flex items-center justify-center gap-2 text-xs text-gray-500 mb-6" data-id="element-238">
                <LockIcon size={12} data-id="element-239" />
                <span data-id="element-240">Secured by 256-bit SSL encryption</span>
                <ShieldCheckIcon size={12} className="text-green-500" data-id="element-241" />
              </div>

              {/* Pay Button */}
              <button onClick={handlePay} className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-bold text-lg hover:from-blue-700 hover:to-indigo-700 transition-all flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]" data-id="element-242">
                <CreditCardIcon size={20} data-id="element-243" />
                Pay ₹{total.toLocaleString()}
              </button>
            </div>
          </>}

        {/* Processing Step */}
        {step === 'processing' && <div className="p-12 flex flex-col items-center justify-center" data-id="element-244">
            <div className="relative w-24 h-24 mb-6" data-id="element-245">
              <div className="absolute inset-0 rounded-full border-4 border-gray-200" data-id="element-246"></div>
              <div className="absolute inset-0 rounded-full border-4 border-blue-500 border-t-transparent animate-spin" data-id="element-247"></div>
              <div className="absolute inset-0 flex items-center justify-center" data-id="element-248">
                <CreditCardIcon className="text-blue-500" size={32} data-id="element-249" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-250">
              Processing Payment
            </h2>
            <p className="text-gray-500 text-center" data-id="element-251">
              Please wait while we process your payment...
            </p>
          </div>}

        {/* Success Step */}
        {step === 'success' && <div className="relative overflow-hidden" data-id="element-252">
            {/* Confetti Animation Background */}
            <div className="absolute inset-0 pointer-events-none" data-id="element-253">
              {[...Array(50)].map((_, i) => <div key={i} className="absolute w-2 h-2 rounded-full animate-confetti" style={{
            left: `${Math.random() * 100}%`,
            backgroundColor: ['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#EC4899'][Math.floor(Math.random() * 6)],
            animationDelay: `${Math.random() * 2}s`,
            animationDuration: `${2 + Math.random() * 2}s`
          }} data-id="element-254" />)}
            </div>

            <div className="relative p-12 flex flex-col items-center justify-center bg-gradient-to-b from-green-50 to-white" data-id="element-255">
              {/* Success Icon with Pulse */}
              <div className="relative mb-6" data-id="element-256">
                <div className="absolute inset-0 bg-green-400 rounded-full animate-ping opacity-25" data-id="element-257"></div>
                <div className="relative w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center shadow-lg animate-bounce-slow" data-id="element-258">
                  <CheckCircleIcon className="text-white" size={48} data-id="element-259" />
                </div>
              </div>

              {/* Celebration Icons */}
              <div className="flex items-center gap-4 mb-4" data-id="element-260">
                <PartyPopperIcon className="text-amber-500 animate-wiggle" size={28} data-id="element-261" />
                <SparklesIcon className="text-purple-500 animate-pulse" size={24} data-id="element-262" />
                <RocketIcon className="text-blue-500 animate-bounce" size={28} data-id="element-263" />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center" data-id="element-264">
                Payment Successful! 🎉
              </h2>

              <p className="text-gray-600 text-center mb-6 max-w-sm" data-id="element-265">
                Congratulations! You've successfully enabled:
              </p>

              {/* Services Enabled */}
              <div className="w-full max-w-sm bg-white rounded-xl border border-gray-200 p-4 mb-6 shadow-sm" data-id="element-266">
                {getServicesList().map((service, idx) => <div key={idx} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0" data-id="element-267">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center" data-id="element-268">
                      <ZapIcon className="text-white" size={16} data-id="element-269" />
                    </div>
                    <span className="text-sm font-medium text-gray-900" data-id="element-270">
                      {service}
                    </span>
                  </div>)}
              </div>

              <p className="text-sm text-gray-500 text-center" data-id="element-271">
                A receipt has been sent to your email
              </p>
            </div>
          </div>}
      </div>

      <style data-id="element-272">{`
        @keyframes confetti {
          0% {
            transform: translateY(-100vh) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        .animate-confetti {
          animation: confetti 4s ease-in-out infinite;
        }
        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
        @keyframes wiggle {
          0%, 100% {
            transform: rotate(-10deg);
          }
          50% {
            transform: rotate(10deg);
          }
        }
        .animate-wiggle {
          animation: wiggle 0.5s ease-in-out infinite;
        }
      `}</style>
    </div>;
}